import React, {
    forwardRef,
    ReactElement,
    ReactNode,
    useState,
    useEffect,
    Children,
    CSSProperties,
} from 'react';
import { styled } from 'styled-components';
import {
    DropDownSelect,
    DropDownSelectOption,
    LabelWithRequired,
    resolveComponentLocator,
    joinDataTestId,
} from '@motili/ui';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';

import {
    SingleValueProps,
    OptionProps,
    SelectComponentsConfig,
    GroupBase,
    SelectInstance,
    Options,
    MenuListProps,
    components as SelectComponents,
} from 'react-select';
import FormValidationDisplay, {
    Validation,
} from 'common/components/form/FormValidationDisplay';
import { getValidationState } from 'common/utils/validation-utils';
import 'common/components/form/FormSelect.overrides.module.scss';
import styles from 'common/components/form/FormSelect.module.scss';
import { generateDefaultDataTestId } from 'common/utils/Utils';
import { List } from 'react-virtualized';
import Config from 'Config';

import { getPortalDataTestId } from '../../utils/data-test-id-utils';

export type Value = {
    value:
        | DropDownSelectOption
        | DropDownSelectOption[]
        | { id: number; display: string }
        | { id: number; display: string }[]
        | { id: string; display: string }
        | { id: string; display: string }[];
};
interface InputGroupConditionalWrapperProps {
    inputGroupBefore?: ReactElement;
    inputGroupAfter?: ReactElement;
    children: any;
}

export type RowRendererProps = {
    key: string;
    index: number;
    style: CSSProperties;
};

interface FormSelectProps {
    label?: string;
    labelStyle?: React.CSSProperties;
    placeholder?: string;
    bsSize?: 'large' | 'small';
    className?: string;
    required?: boolean;
    optional?: boolean;
    validation?: Validation;
    controlId?: string;
    // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
    value?: string | [] | Object | number | null;
    options: any;
    disabled?: boolean;
    onChange?: (value: any) => void;
    onBlur?: () => void;
    multiple?: boolean;
    clearable?: boolean;
    searchable?: boolean;
    autoBlur?: boolean;
    inputGroupBefore?: ReactElement;
    inputGroupAfter?: ReactElement;
    valueRenderer?: (option: any) => JSX.Element;
    optionRenderer?: (option: any) => JSX.Element;
    button?: ReactNode;
    virtualize?: boolean;
    name?: string;
    selectValue?: (value: Value) => void;
    style?: React.CSSProperties;
    closeOnSelect?: boolean;
    isLoading?: boolean;
    onInputChange?: (value: any) => void;
    onMenuScrollToBottom?: (value: any) => void;
    getOptionValue?: (option: any) => string;
    getOptionLabel?: (option: any) => string;
    filterOption?: (
        option: DropDownSelectOption,
        inputValue: string
    ) => boolean;
    componentOverrides?: Partial<
        SelectComponentsConfig<unknown, boolean, GroupBase<unknown>>
    >;
    defaultValue?: DropDownSelectOption | DropDownSelectOption[];
    menuPortalTarget?: HTMLElement;
    menuPosition?: 'fixed' | 'absolute';
    menuShouldScrollIntoView?: boolean;
    dataTestId?: string;
    isAdminLegacyLocator?: boolean;
    hasGroupedOptions?: boolean;
    selectAllOption?: boolean;
}

const InputGroupConditionalWrapper = (
    props: InputGroupConditionalWrapperProps
) => {
    if (props.inputGroupBefore || props.inputGroupAfter) {
        return (
            <div className={styles.dropdownButton}>
                <InputGroup style={{ width: '100%' }}>
                    {props.children}
                </InputGroup>
            </div>
        );
    }
    return <span>{props.children}</span>;
};

const findOptionById = (
    options: any[],
    id: any,
    isMultiple: boolean = false,
    hasGroupedOptions: boolean = false
) => {
    if (isMultiple) {
        const ids = id;
        return ids?.map((id: any) => options?.find(option => option.id === id));
    }
    if (hasGroupedOptions) {
        const group = options?.find(group =>
            group?.options?.some((option: { id: unknown }) => option.id === id)
        );
        return group?.options?.find(
            (option: { id: unknown }) => option.id === id
        );
    }
    return options?.find(option => option.id === id);
};

const typeCheckForValue = (value: any): boolean => {
    if (typeof value !== 'object') {
        return true;
    }
    if (!Array.isArray(value)) {
        return !value.id && !value.display;
    }
    return !value[0] || typeof value[0] !== 'object' || !value[0].id;
};

export const getOptionValueDefault = (option: any) =>
    option.userId || option.id || option.property;
export const getOptionLabelDefault = (option: any) =>
    option.display ||
    option.description ||
    option.name ||
    option.fullName ||
    option.text ||
    option.rawAddress ||
    '';

const FormSelect = forwardRef<
    SelectInstance<unknown, boolean, GroupBase<unknown>>,
    FormSelectProps
>((props, ref) => {
    const { isAdminLegacyLocator = true } = props;
    const [selectedValue, setSelectedValue] = useState(
        props.value && typeCheckForValue(props.value)
            ? findOptionById(
                  props.options,
                  props.value,
                  props.multiple,
                  props.hasGroupedOptions
              )
            : props.value
    );

    const components: Partial<
        SelectComponentsConfig<unknown, boolean, GroupBase<unknown>>
    > = {};

    if (props.valueRenderer) {
        components.SingleValue = (singleValueProps: SingleValueProps<any>) =>
            props.valueRenderer!(singleValueProps.data);
    }

    if (props.optionRenderer) {
        components.Option = (optionProps: OptionProps<any, boolean>) =>
            props.optionRenderer!(optionProps);
    }

    if (props.multiple && props.selectAllOption) {
        components.MenuList = menuListProps =>
            MenuListWithSelectAll(menuListProps);
    }

    useEffect(() => {
        if (props.value === null || props.value === undefined) {
            setSelectedValue(null);
        } else {
            const _value = typeCheckForValue(props.value)
                ? findOptionById(
                      props.options,
                      props.value,
                      props.multiple,
                      props.hasGroupedOptions
                  )
                : props.value;
            setSelectedValue(_value);
        }
    }, [props.value, props.options, props.multiple]);

    const getOptionValue = props.getOptionValue || getOptionValueDefault;
    const getOptionLabel = props.getOptionLabel || getOptionLabelDefault;
    const prefixTestId = getPortalDataTestId(
        props.dataTestId || props.controlId || 'form-select'
    );
    const { validationFeedbackType } = getValidationState(props.validation);

    return (
        <Form.Group
            className={props.className}
            controlId={props.dataTestId || props.controlId}
        >
            <div className={styles.labelContainer}>
                <div>
                    {props.label && (
                        <LabelWithRequired
                            style={props.labelStyle}
                            label={props.label}
                            required={props.required}
                            dataTestId={joinDataTestId([
                                prefixTestId,
                                resolveComponentLocator(
                                    '',
                                    'select',
                                    prefixTestId
                                ),
                            ])}
                        />
                    )}
                </div>
                <div>{props.button}</div>
            </div>
            <InputGroupConditionalWrapper {...props}>
                {props.inputGroupBefore || null}
                <div
                    className='form-select-overrides'
                    data-test-id={joinDataTestId([
                        prefixTestId,
                        resolveComponentLocator(
                            isAdminLegacyLocator
                                ? 'dropdown'
                                : 'select-dropdown',
                            'select-dropdown',
                            prefixTestId
                        ),
                    ])}
                >
                    <StyledDropDownSelect
                        {...props}
                        value={selectedValue}
                        label=''
                        className={
                            props.inputGroupBefore ? styles.select : undefined
                        }
                        isMulti={props.multiple}
                        isClearable={props.clearable}
                        isDisabled={props.disabled}
                        closeMenuOnSelect={props.closeOnSelect}
                        isSearchable={props.searchable}
                        blurInputOnSelect={props.autoBlur}
                        getOptionLabel={getOptionLabel}
                        getOptionValue={getOptionValue}
                        onChange={(value: any) => {
                            setSelectedValue(value);
                            if (props.selectValue) {
                                props.selectValue(value);
                            }
                            if (props.onChange) {
                                props.onChange(value);
                            }
                        }}
                        ref={ref}
                        componentOverrides={{
                            ...props.componentOverrides,
                            ...components,
                        }}
                        onInputChange={props.onInputChange}
                        onMenuScrollToBottom={props.onMenuScrollToBottom}
                        isLoading={props.isLoading}
                        filterOption={props.filterOption}
                        controlId={
                            prefixTestId ||
                            generateDefaultDataTestId(
                                props.label,
                                'form-select-dropdown'
                            )
                        }
                    />
                </div>
                {props.inputGroupAfter || null}
            </InputGroupConditionalWrapper>
            {props.validation?.state && (
                <Form.Control.Feedback
                    type={validationFeedbackType}
                    className='d-block'
                >
                    <FormValidationDisplay
                        validation={props.validation}
                        style={{ ...props.style, marginRight: 40 }}
                        hideIcon
                    />
                </Form.Control.Feedback>
            )}
            {props.optional && <span className='input-optional'>Optional</span>}
        </Form.Group>
    );
});

FormSelect.defaultProps = {
    label: '',
    controlId: 'form-select-input',
    validation: { state: null },
    multiple: false,
    clearable: false,
    searchable: true,
    autoBlur: true,
    virtualize: false,
};

export default FormSelect;

const MenuListWithSelectAll = (menuListProps: MenuListProps<any>) => {
    const { children, getValue, options, setValue, selectProps } =
        menuListProps;
    const { className } = selectProps;
    const rows = Children.toArray(children) as ReactElement<OptionProps>[];
    const selectedLength = getValue().length;
    const selectFunc = () => {
        if (options.length === selectedLength) {
            setValue([], 'select-option');
            return;
        }
        setValue(options, 'select-option');
    };
    const rowRenderer = ({ key, index, style }: RowRendererProps) => {
        const option = rows[index];
        return option.props.label === 'Select All' ? (
            <div key={key} style={{ position: 'relative' }}>
                <SelectAll
                    options={options}
                    getValue={getValue}
                    selectFunc={selectFunc}
                />
            </div>
        ) : (
            <div key={key} style={style}>
                {rows[index]}
            </div>
        );
    };

    return className === 'virtualizedList' ? (
        <List
            style={{ width: '100%' }}
            width={1000}
            height={300}
            rowHeight={40}
            rowCount={rows.length}
            rowRenderer={rowRenderer}
        />
    ) : (
        <SelectComponents.MenuList {...menuListProps}>
            <SelectAll
                options={options}
                getValue={getValue}
                selectFunc={selectFunc}
            />
            {rows}
        </SelectComponents.MenuList>
    );
};

export const SelectAll = (props: {
    selectFunc: () => void;
    getValue: () => Options<unknown>;
    options: Options<unknown>;
}) => {
    return (
        <SelectAllDiv
            role='button'
            tabIndex={0}
            onClick={() => props.selectFunc()}
            onKeyDown={e => {
                if (e.key === 'Enter') props.selectFunc();
            }}
        >
            {props.options.length === props.getValue().length
                ? 'Deselect All'
                : 'Select All'}
        </SelectAllDiv>
    );
};

const SelectAllDiv = styled.div`
    margin: 7px 0 7px 14px;
    cursor: pointer;
    color: ${Config.color.motiliLightBlue};
    display: flex;
`;

const StyledDropDownSelect = styled(DropDownSelect)<{
    validation?: Validation;
}>`
    .select-wrapper__menu-portal {
        z-index: 9;
    }

    ${({ validation }) => {
        if (validation?.state === 'error') {
            return `
        .select-wrapper__control:not(.select-wrapper__control--is-focused):not(.select-wrapper__control--menu-is-open){
          border-color: #e63232 !important;
          box-shadow: 0 0 6px 0 #e6323252 !important;
        }
        `;
        }
        return undefined;
    }}
`;
